import { useEffect } from 'react'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useAuth } from '@/providers/AuthProvider'
import { LoadingScreen } from '@/components/common/Spinner'
import { AppShell } from '@/components/layout/AppShell'
import { AccessGate } from '@/components/features/auth/AccessGate/AccessGate'
import { isUserUnmapped } from '@/lib/access-gate'

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isLoading && !context.auth.isAuthenticated) {
      throw redirect({ to: '/login' })
    }
  },
  component: AuthenticatedLayout,
})

function AuthenticatedLayout() {
  const { user, isLoading, isAuthenticated } = useAuth()
  const navigate = useNavigate()

  // Safety net: redirect if auth finished loading and user is not authenticated.
  // beforeLoad only runs on navigation, not when auth context changes on the
  // current route — this handles the race where beforeLoad ran while isLoading
  // was still true.
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate({ to: '/login' })
    }
  }, [isLoading, isAuthenticated, navigate])

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!isAuthenticated) {
    return null // Redirect is in-flight
  }

  if (user && !user.is_active) {
    return (
      <div
        className="flex flex-col items-center justify-center"
        style={{
          height: '100vh',
          padding: '20px',
          textAlign: 'center',
        }}
      >
        <h1 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>
          Account Deactivated
        </h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          Your account has been deactivated. Please contact an administrator.
        </p>
      </div>
    )
  }

  // Access gate (#658/#699): a `user`-role account must be fully mapped — a
  // manager, a territory AND a region. If any is missing, block ZRM behind a
  // non-dismissible modal until an admin finishes setup. The app renders frozen
  // behind the modal overlay.
  if (isUserUnmapped(user)) {
    return (
      <>
        <AppShell />
        <AccessGate />
      </>
    )
  }

  return <AppShell />
}
